import { AccommodationObject } from "../../../accommodation/infrastructure/api/accommodation.api";
import { Bundle } from "../../../../gen/witbooking/backoffice/bundle/v1/bundle_api_pb";
import { ExtraLegacyEntity } from "../../../../gen/witbooking/backoffice/extra/v1/extra_api_pb";
import { Package } from "../../../../gen/witbooking/backoffice/package/v1/package_api_pb";
import { Tag, TagAppliesToType, TickerWithPosition } from "../../domain/Tag";
import { TagAssociation } from "../../domain/TagAssociation";
import { Translation } from "../../domain/Translation";

type GrpcTranslation = {
  getLocale(): string;
  getName(): string;
};

export const toTagAssociationFromProperty = (
  propertyTicker: string,
  tags: Tag[]
): TagAssociation => {
  const tagsByProperty = getTagsByElement(tags, "property");

  return TagAssociation.of(
    propertyTicker,
    [Translation.of("es", propertyTicker), Translation.of("en", propertyTicker)],
    "property",
    tagsByProperty.get(propertyTicker)
  );
};

export const toTagAssociationFromAccommodationGrpc = (
  accommodations: AccommodationObject[],
  tags: Tag[]
): TagAssociation[] => {
  const tagsByAccommodations = getTagsByElement(tags, "accommodation");

  return accommodations.map(accommodation => {
    return TagAssociation.of(
      accommodation.ticker,
      accommodation.translationsList.map(t => Translation.of(t.locale, t.name)),
      "accommodation",
      tagsByAccommodations.get(accommodation.ticker)
    );
  });
};

export const toTagAssociationFromExtrasGrpc = (
  extras: ExtraLegacyEntity[],
  tags: Tag[]
): TagAssociation[] => {
  const tagsByExtras = getTagsByElement(tags, "extra");

  return extras.map(extra => {
    return TagAssociation.of(
      extra.getTicker(),
      [Translation.of("es", extra.getName()), Translation.of("en", extra.getName())], // TODO - change when extras have translations
      "extra",
      tagsByExtras.get(extra.getTicker())
    );
  });
};

export const toTagAssociationFromBundlesGrpc = (
  bundles: Bundle[],
  tags: Tag[]
): TagAssociation[] => {
  const tagsByBundles = getTagsByElement(tags, "package");

  return bundles.map(bundle => {
    return TagAssociation.of(
      bundle.getTicker(),
      toTranslationsFromGrpcTranslations(bundle.getTranslationsList()),
      "package",
      tagsByBundles.get(bundle.getTicker())
    );
  });
};

const getTagsByElement = (tags: Tag[], type: TagAppliesToType): Map<string, Tag[]> => {
  const tagsByElementWithPosition = tags
    .map(tag => {
      const appliesTo = tag.appliesTo.find(at => at.type === type);
      return [tag, appliesTo?.tickers ?? []] as [Tag, TickerWithPosition[]];
    })
    .reduce((acc, [tag, tickers]) => {
      tickers.forEach(it => {
        const tagsByTicker = acc.get(it.ticker) ?? [];
        tagsByTicker.push([it.position, tag]);
        acc.set(it.ticker, tagsByTicker);
      });
      return acc;
    }, new Map<string, [number, Tag][]>());

  const tagsByElementOrdered = new Map<string, Tag[]>();
  tagsByElementWithPosition.forEach((tagsWithPosition, ticker) => {
    const sortedTags = tagsWithPosition
      .sort(([position1], [position2]) => position1 - position2)
      .map(([position, tag]) => tag);
    tagsByElementOrdered.set(ticker, sortedTags);
  });

  return tagsByElementOrdered;
};

const toTranslationsFromGrpcTranslations = (translations: GrpcTranslation[]) => {
  return translations.map(translation =>
    Translation.of(translation.getLocale(), translation.getName())
  );
};
